import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import DataTable from 'components/Table/DataTable';
import Chip from '@material-ui/core/Chip';
import { getIssuesByElderByCreatedAt, getIssuesByClientByCreatedAt } from 'graphql/queries';
import SocialWorkerDiaryEditButton from 'forms/SocialWorkerDiaryForm/SocialWorkerDiaryEditButton';
import moment from 'moment-timezone';
import { issueStatus, TIME_ZONE } from '@silvergatedelivery/constants';
import { formatComments } from 'utilities/format';
import cache from 'utilities/cache';
import { getEldersByClientByStatus } from 'graphql/queries';
import { asyncListAll } from 'utilities/graph';

const lastMonth = moment().tz(TIME_ZONE).add(-30, 'days').format('YYYY-MM-DD');
const startOfYear = moment().tz(TIME_ZONE).startOf('year').format('YYYY-MM-DD');
const todayDate = moment().tz(TIME_ZONE).format('YYYY-MM-DD');

export default function SocialWorkerDiaryTable({
  title = '社工日誌',
  elder,
  ...props
}) {
  const [indexes, setIndexes] = useState();
  const [queryParams, setQueryParams] = useState();
  const [elders, setElders] = useState([]);
  const clientName = cache.get('app:name');
  const clientId = cache.get('app:facilityId');

  const columns = [
    {
      name: 'id',
      label: '編號',
      options: {
        display: false,
        filter: false,
        sort: true,
      },
    },
    {
      name: 'elderId',
      label: '送餐對象',
      options: {
        display: true,
        filter: false,
        sort: true,
        customBodyRender: (value) => {
          if (elders?.length) {
            return elders?.find((e) => e.id === value)?.name || value;
          }
          return value;
        },
      },
    },
    {
      name: 'executedAt',
      label: '時間',
      options: {
        display: true,
        filter: false,
        sort: true,
        customBodyRender: (value, tableMeta) => {
          if (value) {
            return moment(value).tz(TIME_ZONE).format('YYYY-MM-DD HH:mm');
          } else {
            // 如果沒有executedAt, 則取createdAt, createdAt必須是倒數第三個item
            return moment(tableMeta.rowData[tableMeta.rowData.length - 3]).tz(TIME_ZONE).format('YYYY-MM-DD HH:mm');
          }
        },
      },
    },
    {
      name: 'status',
      label: '狀態',
      options: {
        display: true,
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          const matchedStatus = issueStatus.find((item)=>item.value === value);
          const label = matchedStatus ? matchedStatus.label : value;
          const backgroundColor = matchedStatus ? matchedStatus.color : undefined;
          const color = backgroundColor ? '#fff' : undefined;

          return (<Chip key={value}
            label={label}
            style={{ color, backgroundColor, height: 22 }}
          />);
        },
        filterOptions: {
          renderValue: (v) => (issueStatus.find(({ value }) => value === v) || {}).label,
        },
        customFilterListOptions: {
          render: (v) => (issueStatus.find(({ value }) => value === v) || {}).label,
        },
      },
    },
    {
      name: 'type',
      label: '類別',
      options: {
        display: true,
        filter: true,
        sort: true,
      },
    },
    {
      name: 'description',
      label: '追蹤事項',
      options: {
        display: true,
        filter: false,
        sort: false,
      },
    },
    {
      name: 'comments',
      label: '處理筆記',
      options: {
        display: false,
        filter: false,
        sort: false,
        customBodyRender: formatComments,
      },
    },
    {
      name: 'createdBy',
      label: '創立者',
      options: {
        display: true,
        filter: false,
        sort: true,
      },
    },
    {
      name: 'updatedBy',
      label: '更新者',
      options: {
        display: false,
        filter: false,
        sort: true,
      },
    },
    {
      name: 'createdAt',
      label: '創立於',
      type: 'datetime',
      options: {
        display: true,
        filter: false,
        sort: true,
      },
    },
    {
      name: 'updatedAt',
      label: '更新於',
      type: 'datetime',
      options: {
        display: false,
        filter: false,
        sort: true,
      },
    },
  ];

  useEffect(() => {
    if (elder) {
      setIndexes([
        {
          name: 'getIssuesByElderByCreatedAt',
          label: '按長輩查詢',
          partitionKey: 'elderId',
          sortKey: 'createdAt',
          fields: [{
            label: '長輩',
            key: 'elderId',
            type: 'select',
            options: [{ value: elder?.id, label: elder?.name }],
            disabled: true,
          }, {
            label: '創建時間',
            key: 'createdAt',
            type: 'datetime',
          }],
          operation: getIssuesByElderByCreatedAt,
        },
      ]);
      setQueryParams({
        elderId: elder.id,
        createdAt: {
          from: startOfYear,
          to: todayDate,
        },
      });
      setElders([elder]);
    } else if (clientId) {
      (async () => {
        const existingElders = await asyncListAll(getEldersByClientByStatus, {
          clientId,
          status: { eq: '使用中' },
        });
        setElders(existingElders);
      })();
      setIndexes([
        {
          name: 'getIssuesByClientByCreatedAt',
          label: '按創建時間查詢',
          partitionKey: 'clientId',
          sortKey: 'createdAt',
          fields: [{
            label: '機構',
            key: 'clientId',
            type: 'select',
            options: [{ value: clientId, label: clientName }],
          }, {
            label: '創建時間',
            key: 'createdAt',
            type: 'datetime',
          }],
          operation: getIssuesByClientByCreatedAt,
        },
      ]);
      setQueryParams({
        clientId,
        createdAt: {
          from: lastMonth,
          to: todayDate,
        },
      });
    }
  }, [elder]);

  const editButton = (props) => {
    return <SocialWorkerDiaryEditButton
      {...props}
      elders={elders}
      defaultElder={elder}
    />;
  };

  const customDownloadData = (columns, data) => {
    const statusIndex = columns.findIndex(({ name }) => name === 'status');
    const commentIndex = columns.findIndex(({ name }) => name === 'comments');
    return data.map((value) => {
      if (statusIndex !== -1 && value?.data[statusIndex]) {
        const matchedStatus = issueStatus.find((item)=>item.value === value.data[statusIndex]);
        value.data[statusIndex] = matchedStatus ? matchedStatus.label : '';
      }
      if (commentIndex !== -1 && value?.data[commentIndex]) {
        value.data[commentIndex] = value.data[commentIndex].items.sort((a, b) => moment(a.executedAt || a.createdAt).diff(moment(b.executedAt || b.createdAt)))
          .map(
            (c) => `${c.username}(${moment(c.executedAt || c.createdAt).format('YYYY-MM-DD HH:mm')}): ${c.content}`,
          ).join('\n');
      }
      return value;
    });
  };

  if (!indexes || !queryParams) return null;

  return (
    <React.Fragment>
      <DataTable
        indexes={indexes}
        queryDefaultParams={queryParams}
        title={title}
        columns={columns}
        editButton={editButton}
        customDownloadData={customDownloadData}
        {...props}
      />
    </React.Fragment>);
}

SocialWorkerDiaryTable.propTypes = {
  title: PropTypes.string,
  elder: PropTypes.object,
};
